import { Component, OnInit } from '@angular/core';
import { RiskFactorModalComponent } from '../risk-factor-modal/risk-factor-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationModalComponent } from '../../../shared/modals/confirmation-modal/confirmation-modal.component';
import { RiskFactorType } from '../../../../models/risk-factor-type';
import { AgilityRiskService } from '../../../../providers/agility-risk.service';
import { AgilityRiskFormulaHelperService } from '../../../../providers/agility-risk-formula-helper.service';
import { RiskFactor } from '../../../../models/risk-factor';
import { AgilityRiskSpinnerService } from '../../../../providers/agility-risk-spinner.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AgilityRiskMessageService } from '../../../../providers/agility-risk-message.service';

@Component({
  selector: 'lib-risk-factors-modal',
  templateUrl: './risk-factors-modal.component.html',
  styleUrls: ['./risk-factors-modal.component.scss']
})
export class RiskFactorsModalComponent implements OnInit {

  public isEditing: boolean;
  public riskFactors: Array<RiskFactor>;
  public riskFactorType = RiskFactorType;
  public formulaId: string;

  constructor(private translateService: TranslateService,
              private dialogService: DialogService,
              private dlgRef: DynamicDialogRef,
              private dialogConfig: DynamicDialogConfig,
              private agilityRiskService: AgilityRiskService,
              private formulaHelper: AgilityRiskFormulaHelperService,
              private messageService: AgilityRiskMessageService,
              private spinner: AgilityRiskSpinnerService) {
  }

  ngOnInit(): void {
    if (this.dialogConfig.data.formulaId) {
      this.formulaId = this.dialogConfig.data.formulaId;
      this.getFactors();
    }
  }

  getFactors() {
    this.spinner.start();
    this.agilityRiskService.getRiskFormulaFactors(this.formulaId).subscribe({
      next: res => {
        this.riskFactors = res;
        this.spinner.stop();
      },
      error: err => {
        this.spinner.stop();
      }
    });
  }

  editFactor(factorId: string) {
    this.isEditing = true;
    const factor = this.riskFactors.find(rf => rf.id === factorId);
    if (!factor) {
      return;
    }
    const dlgRef = this.dialogService.open(
      RiskFactorModalComponent,
      {
        data: {
          formulaId: this.formulaId,
          riskFactors: this.riskFactors,
          factor
        },
        header: this.translateService.instant('formulaManagement.editFactorModal.header'),
        width: '50%'
      });

    dlgRef.onClose.subscribe(res => {
      if (res) {
        this.getFactors();
      }
    });

    dlgRef.onDestroy.subscribe(res => {
      this.isEditing = false;
    });
  }

  deleteFactor(factorId: string) {
    const indx = this.riskFactors.findIndex(rf => rf.id === factorId);
    if (indx < 0) {
      return;
    }

    const confDlg = this.dialogService.open(ConfirmationModalComponent,
      {
        data: {
          body: this.translateService.instant('formulaManagement.editFactorsModal.deleteFactorBody')
        },
        header: this.translateService.instant('formulaManagement.editFactorsModal.deleteFactor'),
        width: '35%'
      });

    confDlg.onClose.subscribe(res => {
      if (res) {
        this.spinner.start();
        this.agilityRiskService.deleteRiskFactor(factorId).subscribe({
          next: deleteRes => {
            this.spinner.stop();
            this.messageService.success(this.translateService.instant('common.deleted'));
            this.getFactors();
            this.formulaHelper.triggerRefreshFactors(this.formulaId);
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.error(this.translateService.instant('formulaManagement.editFactorsModal.deleteFactorErrorMsg'));
            this.spinner.stop();
          }
        });
      }
    });
  }

  onCreateNewFactor() {
    this.isEditing = true;
    const subscription = this.formulaHelper.subscribeToRefreshFactor(this.getFactors.bind(this));
    const dlgRef = this.dialogService.open(
      RiskFactorModalComponent,
      {
        data: {
          formulaId: this.formulaId,
          riskFactors: this.riskFactors,
        },
        header: this.translateService.instant('formulaManagement.editFactorModal.header'),
        width: '50%'
      });

    dlgRef.onClose.subscribe(res => {
      subscription.unsubscribe();
    });

    dlgRef.onDestroy.subscribe(res => {
      this.isEditing = false;
      subscription.unsubscribe();
    });
  }

  onClose() {
    this.dlgRef.close(true);
  }
}
